import React, { useState } from 'react';
import { Box, Typography, Grid, Container, Chip, TextField, Button, MenuItem } from '@mui/material';
import BedIcon from '@mui/icons-material/Bed';
import BathtubIcon from '@mui/icons-material/Bathtub';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VerifiedIcon from '@mui/icons-material/Verified';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';

const properties = [
  { name: '3BHK Luxury Villa', location: 'Whitefield, Bangalore', price: '₹85L', beds: 3, baths: 2, sqft: '1850 sq.ft', tag: 'Ready to Move', tagColor: '#10b981', type: 'Residential', img: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=400&h=250&fit=crop' },
  { name: 'Premium 2BHK Flat', location: 'Electronic City, Bangalore', price: '₹52L', beds: 2, baths: 2, sqft: '1200 sq.ft', tag: 'Under Construction', tagColor: '#f59e0b', type: 'Residential', img: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=250&fit=crop' },
  { name: 'Commercial Office Space', location: 'Koramangala, Bangalore', price: '₹1.2Cr', beds: null, baths: null, sqft: '3500 sq.ft', tag: 'New Launch', tagColor: '#6366f1', type: 'Commercial', img: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&h=250&fit=crop' },
  { name: '4BHK Independent House', location: 'Sarjapur, Bangalore', price: '₹1.4Cr', beds: 4, baths: 3, sqft: '2800 sq.ft', tag: 'Ready to Move', tagColor: '#10b981', type: 'Residential', img: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=400&h=250&fit=crop' },
  { name: 'Retail Shop / Showroom', location: 'Indiranagar, Bangalore', price: '₹75L', beds: null, baths: null, sqft: '850 sq.ft', tag: 'New Launch', tagColor: '#6366f1', type: 'Commercial', img: 'https://images.unsplash.com/photo-1582647509711-c8aa8a8bda71?w=400&h=250&fit=crop' },
  { name: '1BHK Studio Apartment', location: 'HSR Layout, Bangalore', price: '₹32L', beds: 1, baths: 1, sqft: '650 sq.ft', tag: 'Under Construction', tagColor: '#f59e0b', type: 'Residential', img: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=400&h=250&fit=crop' },
];

const filterTypes = ['All', 'Residential', 'Commercial'];
const sortOptions = ['Default', 'Price: Low to High', 'Price: High to Low'];

const LivePreview = () => {
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [sort, setSort] = useState('Default');

  const filtered = properties
    .filter(p => {
      const matchesType = filterType === 'All' || p.type === filterType;
      const matchesSearch = search === '' ||
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.location.toLowerCase().includes(search.toLowerCase()) ||
        p.tag.toLowerCase().includes(search.toLowerCase());
      return matchesType && matchesSearch;
    })
    .sort((a, b) => {
      if (sort === 'Price: Low to High') return parseFloat(a.price.replace(/[₹LCr]/g, '')) - parseFloat(b.price.replace(/[₹LCr]/g, ''));
      if (sort === 'Price: High to Low') return parseFloat(b.price.replace(/[₹LCr]/g, '')) - parseFloat(a.price.replace(/[₹LCr]/g, ''));
      return 0;
    });

  return (
    <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#f8f7ff' }}>
      <Container maxWidth="lg">
        {/* Section header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography sx={{ color: '#4338ca', fontWeight: 700, fontSize: 13, letterSpacing: 2, mb: 1.5, fontFamily: "'Montserrat', sans-serif", textTransform: 'uppercase' }}>Live Property Listings</Typography>
          <Typography variant="h2" sx={{ fontWeight: 800, color: '#1e1b4b', fontFamily: "'Montserrat', sans-serif", fontSize: { xs: '1.8rem', md: '2.6rem' } }}>
            Premium Property Cards, Ready to Go
          </Typography>
        </Box>

        {/* ── SEARCH & FILTER BAR ── */}
        <Box sx={{
          bgcolor: '#ffffff',
          borderRadius: '20px',
          p: { xs: 2, sm: 2.5 },
          mb: 6,
          boxShadow: '0 8px 40px rgba(99,102,241,0.1)',
          border: '1px solid #e0e7ff',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          position: 'relative',
          zIndex: 1,
        }}>
          {/* Search Text Field */}
          <TextField
            placeholder="Search by name, location or type..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: '#6366f1', mr: 1, fontSize: 22 }} />,
            }}
            sx={{
              flex: 1,
              minWidth: { xs: '100%', sm: 200 },
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontFamily: "'Montserrat', sans-serif",
                fontSize: 14.5,
                bgcolor: '#f8fafc',
                '& fieldset': { borderColor: '#e0e7ff' },
                '&:hover fieldset': { borderColor: '#6366f1' },
                '&.Mui-focused fieldset': { borderColor: '#4338ca', borderWidth: 2 },
              },
              '& input': { fontFamily: "'Montserrat', sans-serif", fontSize: 14.5, color: '#0f172a' },
            }}
          />

          {/* Type Filter */}
          <TextField
            select
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            size="small"
            label="Type"
            sx={{
              minWidth: 140,
              flexShrink: 0,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontFamily: "'Montserrat', sans-serif",
                fontSize: 14,
                bgcolor: '#f8fafc',
                '& fieldset': { borderColor: '#e0e7ff' },
                '&:hover fieldset': { borderColor: '#6366f1' },
                '&.Mui-focused fieldset': { borderColor: '#4338ca', borderWidth: 2 },
              },
              '& .MuiInputLabel-root': { fontFamily: "'Montserrat', sans-serif", fontSize: 14 },
              '& select, & .MuiSelect-select': { fontFamily: "'Montserrat', sans-serif", fontSize: 14 },
            }}
          >
            {filterTypes.map(t => (
              <MenuItem key={t} value={t} sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: 14 }}>{t}</MenuItem>
            ))}
          </TextField>

          {/* Sort Filter */}
          <TextField
            select
            value={sort}
            onChange={e => setSort(e.target.value)}
            size="small"
            label="Sort By"
            sx={{
              minWidth: 170,
              flexShrink: 0,
              display: { xs: 'none', md: 'block' },
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontFamily: "'Montserrat', sans-serif",
                fontSize: 14,
                bgcolor: '#f8fafc',
                '& fieldset': { borderColor: '#e0e7ff' },
                '&:hover fieldset': { borderColor: '#6366f1' },
                '&.Mui-focused fieldset': { borderColor: '#4338ca', borderWidth: 2 },
              },
              '& .MuiInputLabel-root': { fontFamily: "'Montserrat', sans-serif", fontSize: 14 },
            }}
          >
            {sortOptions.map(o => (
              <MenuItem key={o} value={o} sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: 14 }}>{o}</MenuItem>
            ))}
          </TextField>

          {/* Search Button */}
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={() => {}}
            sx={{
              bgcolor: '#4338ca', color: '#fff',
              px: 3, py: 1.15,
              borderRadius: '12px',
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 700, fontSize: 14,
              whiteSpace: 'nowrap', flexShrink: 0,
              '&:hover': { bgcolor: '#3730a3' },
              boxShadow: '0 4px 14px rgba(67,56,202,0.3)',
            }}
          >
            Search
          </Button>
        </Box>

        {/* Result count */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography sx={{ color: '#64748b', fontSize: 14, fontFamily: "'Montserrat', sans-serif", fontWeight: 600 }}>
            {filtered.length} {filtered.length === 1 ? 'property' : 'properties'} found
            {filterType !== 'All' ? ` in ${filterType}` : ''}
            {search ? ` for "${search}"` : ''}
          </Typography>
          {/* Active filters chips */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {filterType !== 'All' && (
              <Chip label={filterType} size="small" onDelete={() => setFilterType('All')} sx={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, bgcolor: '#eef2ff', color: '#4338ca' }} />
            )}
            {search && (
              <Chip label={`"${search}"`} size="small" onDelete={() => setSearch('')} sx={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e' }} />
            )}
          </Box>
        </Box>

        {/* Property cards — ALL SAME HEIGHT via alignItems stretch + height 100% */}
        {filtered.length > 0 ? (
          <Grid container spacing={3} alignItems="stretch">
            {filtered.map((p, i) => (
              <Grid item xs={12} sm={6} md={4} key={i} sx={{ display: 'flex' }}>
                <Box sx={{
                  width: '100%',
                  display: 'flex', flexDirection: 'column',
                  bgcolor: '#fff', borderRadius: '20px', overflow: 'hidden',
                  boxShadow: '0 4px 24px rgba(99,102,241,0.08)',
                  border: '1px solid rgba(99,102,241,0.08)',
                  transition: 'all 0.3s',
                  '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 20px 50px rgba(99,102,241,0.15)' },
                }}>
                  {/* Image — fixed height */}
                  <Box sx={{ position: 'relative', flexShrink: 0 }}>
                    <img src={p.img} alt={p.name} style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
                    <Chip label={p.tag} size="small" sx={{ position: 'absolute', top: 12, left: 12, bgcolor: p.tagColor, color: '#fff', fontWeight: 700, fontFamily: "'Montserrat', sans-serif" }} />
                    <Box sx={{ position: 'absolute', top: 12, right: 12, width: 36, height: 36, borderRadius: '50%', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
                      <FavoriteIcon sx={{ color: '#e2e8f0', fontSize: 18, '&:hover': { color: '#ef4444' } }} />
                    </Box>
                  </Box>

                  {/* Content — grows to fill remaining height */}
                  <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography sx={{ fontWeight: 800, color: '#0f172a', fontFamily: "'Montserrat', sans-serif", fontSize: '1rem' }}>{p.name}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, flexShrink: 0 }}>
                        <VerifiedIcon sx={{ color: '#6366f1', fontSize: 16 }} />
                        <Typography sx={{ color: '#6366f1', fontSize: 11, fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}>Verified</Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                      <LocationOnIcon sx={{ color: '#94a3b8', fontSize: 15 }} />
                      <Typography sx={{ color: '#64748b', fontSize: 13, fontFamily: "'Montserrat', sans-serif" }}>{p.location}</Typography>
                    </Box>

                    {/* Specs row — fixed height placeholder even for commercial */}
                    <Box sx={{ minHeight: 28, mb: 2 }}>
                      {p.beds && (
                        <Box sx={{ display: 'flex', gap: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><BedIcon sx={{ color: '#94a3b8', fontSize: 17 }} /><Typography sx={{ color: '#475569', fontSize: 13, fontFamily: "'Montserrat', sans-serif" }}>{p.beds} Beds</Typography></Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><BathtubIcon sx={{ color: '#94a3b8', fontSize: 17 }} /><Typography sx={{ color: '#475569', fontSize: 13, fontFamily: "'Montserrat', sans-serif" }}>{p.baths} Baths</Typography></Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><SquareFootIcon sx={{ color: '#94a3b8', fontSize: 17 }} /><Typography sx={{ color: '#475569', fontSize: 13, fontFamily: "'Montserrat', sans-serif" }}>{p.sqft}</Typography></Box>
                        </Box>
                      )}
                      {!p.beds && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <SquareFootIcon sx={{ color: '#94a3b8', fontSize: 17 }} />
                          <Typography sx={{ color: '#475569', fontSize: 13, fontFamily: "'Montserrat', sans-serif" }}>{p.sqft} · Commercial</Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Price + CTA — always at bottom */}
                    <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 2, borderTop: '1px solid #f1f5f9' }}>
                      <Typography sx={{ color: '#4338ca', fontWeight: 900, fontSize: '1.3rem', fontFamily: "'Montserrat', sans-serif" }}>{p.price}</Typography>
                      <Box sx={{ bgcolor: '#eef2ff', color: '#4338ca', px: 2, py: 0.6, borderRadius: '8px', fontSize: 13, fontFamily: "'Montserrat', sans-serif", fontWeight: 700, cursor: 'pointer', '&:hover': { bgcolor: '#4338ca', color: '#fff' }, transition: 'all 0.2s' }}>
                        Enquire
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography sx={{ color: '#94a3b8', fontSize: 18, fontFamily: "'Montserrat', sans-serif", fontWeight: 600 }}>No properties match your search.</Typography>
            <Button onClick={() => { setSearch(''); setFilterType('All'); }} sx={{ mt: 2, color: '#4338ca', fontFamily: "'Montserrat', sans-serif", fontWeight: 700 }}>Clear Filters</Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default LivePreview;
